import { z } from 'zod';
import { SubcontractStatus } from './subcontracting.js';
import { Action, ClientMessage, NoSubscriptionReason } from './actions.js';
import { Cents, IsoDate, IsoDateTime, Uuid } from './common.js';
import { ProjectSummary } from './projects.js';

/**
 * Lot AV « Page client » (CDC, évolutions du 17/10/2026 ; maquette `devis-r2-1710.html`, onglet C, repères 20-30).
 * Une page par client qui NE RECOPIE PAS Axonaut : résumé de l'assistant, rendez-vous et fiches R1, mails, projet,
 * cartes, site et abonnement (transmis par le back Evolusite), devis et factures en montants + liens Axonaut.
 * Logique pure associée : `packages/shared/src/clientPage.ts`.
 */

// ------------------------------------------------------------------ Relation et annuaire

/**
 * Relation avec le client, d'après le statut de la Carte (`mapClientStatus`) : `client` (abonné, ou devis signé,
 * site, facture, date de démarrage), `former` (abonnement résilié ou client parti), `prospect` (devis en attente
 * seulement, ou rien encore).
 */
export const ClientRelation = z.enum(['client', 'former', 'prospect']);
export type ClientRelation = z.infer<typeof ClientRelation>;

export const CLIENT_RELATION_LABELS: Record<ClientRelation, string> = {
  client: 'Client',
  former: 'Ancien client',
  prospect: 'Prospect',
};

/** Une ligne de la recherche de client de l'en-tête (`GET /api/clients/directory`). */
export const ClientDirectoryItem = z.object({
  id: Uuid,
  name: z.string(),
  city: z.string().nullable(),
  relation: ClientRelation,
});
export type ClientDirectoryItem = z.infer<typeof ClientDirectoryItem>;

export const ClientDirectory = z.object({ items: z.array(ClientDirectoryItem) });
export type ClientDirectory = z.infer<typeof ClientDirectory>;

// ------------------------------------------------------------------ En-tête

export const ClientHeader = z.object({
  id: Uuid,
  name: z.string(),
  /** Commune (géocodage, sinon lue dans l'adresse). */
  city: z.string().nullable(),
  address: z.string().nullable(),
  relation: ClientRelation,
  /** Client depuis : date de démarrage, de mise en ligne ou du premier devis signé ; prospect : premier devis. */
  since: IsoDate.nullable(),
  /** Formule de l'abonnement en cours (Stripe via le back, sinon abonnement hors Stripe), null sans abonnement. */
  plan: z.string().nullable(),
  planMonthlyCents: Cents.nullable(),
  /** Au moins un site du client est en ligne (`isLive` du back). */
  siteOnline: z.boolean(),
  /** Projet en cours (non terminé), le premier dans l'ordre du planning. */
  activeProject: z.object({ id: Uuid, name: z.string() }).nullable(),
  /** Fiche du client dans Axonaut (`clients.axonaut_company_id`), null si inconnue. */
  axonautUrl: z.string().nullable(),
  /** Lot AI : « Pas d'abonnement à mettre » noté sur ce client. */
  noSubscriptionReason: NoSubscriptionReason.nullable(),
});
export type ClientHeader = z.infer<typeof ClientHeader>;

// ------------------------------------------------------------------ Rendez-vous

export const ClientMeetingKind = z.enum(['R1', 'R2', 'R3', 'R4', 'R5', 'ONLINE', 'OTHER']);
export type ClientMeetingKind = z.infer<typeof ClientMeetingKind>;

export const ClientMeeting = z.object({
  /** Identifiant de la copie principale (`calendar_events.id`). */
  id: Uuid,
  kind: ClientMeetingKind,
  title: z.string(),
  startAt: IsoDateTime,
  endAt: IsoDateTime,
  allDay: z.boolean(),
  location: z.string().nullable(),
  /** Membres dont l'agenda contient ce rendez-vous. */
  memberIds: z.array(Uuid),
  /** R1 : clé du volet R1 de l'agenda (`/agenda?r1=<clé>`) ; null pour les autres rendez-vous. */
  r1Key: z.string().nullable(),
  /** R1 : fiche R1 prête (`r1_briefs` READY). */
  hasBrief: z.boolean(),
});
export type ClientMeeting = z.infer<typeof ClientMeeting>;

export const ClientMeetings = z.object({
  /** À venir, du plus proche au plus lointain (10 au plus). */
  upcoming: z.array(ClientMeeting),
  /** Passés, du plus récent au plus ancien (10 au plus). */
  past: z.array(ClientMeeting),
});
export type ClientMeetings = z.infer<typeof ClientMeetings>;

// ------------------------------------------------------------------ Mails

/** Mails du client (mêmes lignes que `GET /api/clients/:id/messages`, 500 plus récents au plus). */
export const ClientMails = z.object({
  /** Nombre total de mails connus (Gmail et Axonaut), au-delà des 500 rendus. */
  total: z.int().min(0),
  received: z.int().min(0),
  sent: z.int().min(0),
  waitingOn: z.enum(['client', 'us']).nullable(),
  lastAt: IsoDateTime.nullable(),
  items: z.array(ClientMessage),
});
export type ClientMails = z.infer<typeof ClientMails>;

// ------------------------------------------------------------------ Site et abonnement (back Evolusite)

export const ClientSite = z.object({
  evolusiteSiteId: z.string(),
  name: z.string().nullable(),
  url: z.string().nullable(),
  onlineAt: IsoDateTime.nullable(),
  /** Dernière connexion à l'admin du site, toutes personnes confondues (le back ne distingue pas l'équipe). */
  lastAdminActivityAt: IsoDateTime.nullable(),
  isLive: z.boolean().nullable(),
  isBuilding: z.boolean().nullable(),
  /** Lien vers le site dans l'admin Evolusite (modèle de la connexion du back), si connu. */
  adminUrl: z.string().nullable(),
});
export type ClientSite = z.infer<typeof ClientSite>;

/** Abonnement Stripe tel que le back le transmet (pas d'historique des paiements). */
export const ClientStripeSubscription = z.object({
  evolusiteSiteId: z.string(),
  planName: z.string().nullable(),
  monthlyAmountCents: Cents,
  interval: z.enum(['month', 'year']).nullable(),
  status: z.string(),
  /** Fin de la période en cours = prochain prélèvement. */
  currentPeriodEnd: IsoDateTime.nullable(),
  cancelAt: IsoDateTime.nullable(),
  pastDueSince: IsoDateTime.nullable(),
});
export type ClientStripeSubscription = z.infer<typeof ClientStripeSubscription>;

/** Abonnement facturé hors Stripe (lot J), saisi dans les Réglages. */
export const ClientManualSubscription = z.object({
  id: Uuid,
  label: z.string(),
  planName: z.string().nullable(),
  amountCents: Cents,
  periodMonths: z.int().min(1),
  startDate: IsoDate,
  endDate: IsoDate.nullable(),
  /** Prochaine date de facturation (null : terminé). */
  nextBillingDate: IsoDate.nullable(),
});
export type ClientManualSubscription = z.infer<typeof ClientManualSubscription>;

export const ClientSubscriptions = z.object({
  stripe: z.array(ClientStripeSubscription),
  manual: z.array(ClientManualSubscription),
});
export type ClientSubscriptions = z.infer<typeof ClientSubscriptions>;

// ------------------------------------------------------------------ Devis et factures (montants + liens)

export const ClientQuote = z.object({
  id: Uuid,
  number: z.string().nullable(),
  title: z.string().nullable(),
  status: z.enum(['pending', 'accepted', 'refused']),
  amountCents: Cents,
  date: IsoDate.nullable(),
  answeredAt: IsoDateTime.nullable(),
  /** « Ouvrir dans Axonaut » : lien du document, sinon la fiche du client ; null si rien n'est connu. */
  axonautUrl: z.string().nullable(),
  /**
   * Lot AY : sous-traitance du devis (`unset` = pas encore renseignée) et sous-traité plafonné (0 sans sous-traitance).
   * Facultatifs pour les anciennes réponses gardées hors connexion.
   */
  subcontractStatus: SubcontractStatus.optional(),
  subcontractedCents: Cents.optional(),
});
export type ClientQuote = z.infer<typeof ClientQuote>;

export const ClientInvoice = z.object({
  id: Uuid,
  number: z.string().nullable(),
  kind: z.enum(['deposit', 'balance', 'single', 'subscription']),
  amountCents: Cents,
  date: IsoDate,
  paidAt: IsoDateTime.nullable(),
  outstandingCents: Cents,
  axonautUrl: z.string().nullable(),
});
export type ClientInvoice = z.infer<typeof ClientInvoice>;

export const ClientMoney = z.object({
  /** Du plus récent au plus ancien (20 au plus). */
  quotes: z.array(ClientQuote),
  invoices: z.array(ClientInvoice),
  /** Totaux sur toutes les lignes du client (HT). Lot AY : `signedCents` en part d'Evolusite. */
  totals: z.object({
    signedCents: Cents,
    /** Lot AY : sous-traité des devis signés (« dont … sous-traités »). */
    subcontractedCents: Cents.optional(),
    pendingCents: Cents,
    invoicedCents: Cents,
    outstandingCents: Cents,
  }),
});
export type ClientMoney = z.infer<typeof ClientMoney>;

// ------------------------------------------------------------------ Résumé de l'assistant

export const ClientSummarySourceKind = z.enum(['mail', 'meeting', 'r1', 'card', 'quote', 'project']);
export type ClientSummarySourceKind = z.infer<typeof ClientSummarySourceKind>;

/** Source d'un point du résumé (lien vers le mail, le rendez-vous, la carte, la fiche R1…). */
export const ClientSummarySource = z.object({
  kind: ClientSummarySourceKind,
  /** Court libellé de la pastille (« mail 15 oct. », « R4 9 oct. », « carte »). */
  label: z.string(),
  /** Titre complet (objet du mail, titre du rendez-vous, de la carte…). */
  title: z.string(),
  /** Lien interne (`/agenda?r1=…`, `/a-faire?action=…`, `/projets?project=…`), ancre de la page ou lien Axonaut. */
  href: z.string().nullable(),
});
export type ClientSummarySource = z.infer<typeof ClientSummarySource>;

export const ClientSummaryPoint = z.object({
  text: z.string(),
  sources: z.array(ClientSummarySource),
});
export type ClientSummaryPoint = z.infer<typeof ClientSummaryPoint>;

export const ClientSummaryContent = z.object({
  /** « Où on en est ». */
  status: z.array(ClientSummaryPoint),
  /** « Ce qu'on a promis ». */
  promises: z.array(ClientSummaryPoint),
});
export type ClientSummaryContent = z.infer<typeof ClientSummaryContent>;

/**
 * État du résumé d'un client. `available` : Claude est configuré (sinon la section est masquée avec une indication).
 * `stale` : aucun résumé, ou plus de 24 h (décision de Maxime) : la page en demande un nouveau à l'ouverture.
 */
export const ClientSummaryState = z.object({
  available: z.boolean(),
  content: ClientSummaryContent.nullable(),
  generatedAt: IsoDateTime.nullable(),
  stale: z.boolean(),
  /** Une rédaction est en cours (lancée il y a moins de 3 minutes). */
  generating: z.boolean(),
  /** Dernière erreur (le résumé précédent reste affiché). */
  error: z.string().nullable(),
  model: z.string().nullable(),
  /** Coût de la dernière rédaction (centimes de dollar). */
  costCents: z.int().nullable(),
});
export type ClientSummaryState = z.infer<typeof ClientSummaryState>;

/** `POST /api/clients/:id/summary` : `force` = « Rafraîchir » (sinon seulement si plus de 24 h). */
export const ClientSummaryRequest = z.object({ force: z.boolean().default(false) });
export type ClientSummaryRequest = z.input<typeof ClientSummaryRequest>;

/**
 * Réponse que Claude doit rendre (schéma donné dans la consigne) : des points courts, chacun avec les
 * identifiants des sources données dans la consigne (`M1`, `R2`, `C3`…). Les identifiants inconnus sont retirés.
 */
export const ClientSummaryDraft = z.object({
  status: z.array(z.object({ text: z.string().min(1).max(400), sources: z.array(z.string()).max(4) })).max(5),
  promises: z
    .array(z.object({ text: z.string().min(1).max(400), sources: z.array(z.string()).max(4) }))
    .max(8),
});
export type ClientSummaryDraft = z.infer<typeof ClientSummaryDraft>;

// ------------------------------------------------------------------ Page entière

/** `GET /api/clients/:id/overview` : tout ce que la page client affiche, en une réponse. */
export const ClientOverview = z.object({
  today: IsoDate,
  client: ClientHeader,
  summary: ClientSummaryState,
  meetings: ClientMeetings,
  mails: ClientMails,
  /** Projets du client : en cours d'abord (ordre du planning), puis terminés. */
  projects: z.array(ProjectSummary),
  /** Cartes ouvertes du client ou de ses projets (« À faire »), y compris les tâches ajoutées à la main. */
  cards: z.array(Action),
  sites: z.array(ClientSite),
  subscriptions: ClientSubscriptions,
  money: ClientMoney,
});
export type ClientOverview = z.infer<typeof ClientOverview>;
